import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { useAuthStore } from "@/stores/Auth";
import { formatLocalePercentage, formatPriceAsCurrency } from "@/helpers/formattingUtilities";

// GraphQL dependencies
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload, ApiFetchResponse } from "@/types/api/fetch";

import { RelatedData, SectorInfo, SectorWithRelatedData, SecurityInfo, SecurityWithRelatedDataBySector, ValueData } from "@/types/related";
import type { SectorRelatedSecuritiesApiResponse, RelatedSecuritiesDataApiResponse, RelatedSecurityApiResponse, SectorWithRelatedSecurities, StockWithRelatedSecuritiesBySectorApiResponse } from "@/types/api/related";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

function createSimpleTableRows( data: RelatedSecurityApiResponse[] ): ValueData[] {
    return data.map( ( data: RelatedSecurityApiResponse ): ValueData => ( {
        title: data.name,
        symbol: data.symbol,
        six_mo_perf: data.six_mo_perf,
        seven_minus_one_mo_perf: data.seven_minus_one_mo_perf,
        last_close: data.close
    } ) );
}

function buildRelatedDataTable( data: RelatedSecurityApiResponse[], isStock: boolean ): RelatedData {
    return {
        headers: [
            { text: 'Ticker', align: 'start', value: 'symbol' },
            { text: isStock ? 'Company' : 'Fund', value: 'title' },
            { text: 'Last Close Price', value: 'last_close' },
            { text: '6 mo %', value: 'six_mo_perf' },
            { text: '7-1 mo %', value: 'seven_minus_one_mo_perf' }
        ],
        rows: createSimpleTableRows( data )
    };
}

function buildSecurityInfo( aggregateData: StockWithRelatedSecuritiesBySectorApiResponse ): SecurityInfo {
    return {
        symbol: aggregateData.symbol,
        name: aggregateData.name,
        six_mo_perf: `${ formatLocalePercentage( aggregateData.six_mo_perf ) }`,
        seven_minus_one_mo_perf: `${ formatLocalePercentage( aggregateData.seven_minus_one_mo_perf ) }`,
        last_close: `${ formatPriceAsCurrency( aggregateData.last_close || 0.0 ) }`,
        market_rs: ( aggregateData.market_rs || '-' )
    };
}

function buildSectorData( sector: SectorRelatedSecuritiesApiResponse ): SectorInfo {
    return {
        id: sector.id,
        symbol: sector.symbol,
        name: sector.name,
        bpiStatus: sector.bpi_status,
        bpiLastStatusChangeDate: sector.bpi_rev_dt,
        bpiSignal: sector.bpi_signal,
        bpiColumn: sector.bpi_column,

        rs: sector.rs,
        rsRank: sector.rs_rank,
        rsColumn: sector.rs_column,
        rsReversalDate: sector.rs_rev_dt
    };
}

function buildRelatedData( dataBySector: SectorRelatedSecuritiesApiResponse | null | undefined ): SectorWithRelatedData | null {
    if ( null == dataBySector ) {
        return null;
    }

    const sector: SectorInfo = buildSectorData( dataBySector );
    const relatedStocks: RelatedData = buildRelatedDataTable( dataBySector.stocks, true );
    const relatedEtfs: RelatedData = buildRelatedDataTable( dataBySector.etfs, false );

    return {
        sector: sector,
        stocks: relatedStocks,
        etfs: relatedEtfs
    };
}

function buildRelatedDataBySector( sectors?: SectorRelatedSecuritiesApiResponse[] ): SectorWithRelatedData[] {
    const relatedData: SectorWithRelatedData[] = [] as unknown as SectorWithRelatedData[];
    sectors?.forEach( ( sectorResponse: SectorRelatedSecuritiesApiResponse | null | undefined ) => {
        const result = buildRelatedData( sectorResponse );
        if ( result ) {
            relatedData.push( result );
        }
    } );

    return relatedData;
}

function buildSecurityWithRelatedDataBySector( aggregateData: StockWithRelatedSecuritiesBySectorApiResponse ): SecurityWithRelatedDataBySector {
    return {
        security: buildSecurityInfo( aggregateData ),
        related_data: buildRelatedDataBySector( aggregateData?.related_securities_by_sector?.sectors )
    }
}

const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
const relatedDataBySector = ref< SecurityWithRelatedDataBySector | null >( null );
const sectorRelatedData = ref< SectorWithRelatedData | null >( null );

export const useSecuritiesStore = defineStore( 'security', () => {
    const { authToken } = storeToRefs( useAuthStore() );

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        loading.value = false;
        relatedDataBySector.value = null;
        sectorRelatedData.value = null;
    }

    async function loadSecurityDataForDate( ticker: string, date: string ): Promise< void > {
        loading.value = true;
        goFetch< RelatedSecuritiesDataApiResponse, PersistedQueryPayload >( {
            serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
            endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
            payload: {
                id: `b48a508a97258c4ffd8f4b8884bd53c3`,
                variables: {
                    date: date,
                    ticker: ticker
                }
            },
            authToken: authToken.value
        } ).then( ( response: ApiFetchResponse< RelatedSecuritiesDataApiResponse > | null | undefined ) => {
            if ( response?.data?.relatedSecuritiesBySector ) {
                const stockWithRelatedSecuritiesBySector: StockWithRelatedSecuritiesBySectorApiResponse = response.data.relatedSecuritiesBySector;
                if ( stockWithRelatedSecuritiesBySector ) {
                    relatedDataBySector.value = buildSecurityWithRelatedDataBySector( stockWithRelatedSecuritiesBySector );
                } else {
                    // TODO: throw this and respond appropriately within the calling component
                    logger.error( `Data was not found for ticker '${ ticker }' for date: ${ date }` );
                }
            }
        } ).finally( () => loading.value = false );
    }

    async function loadSectorDataForDate( symbol: string, dateAsString: string ): Promise< void > {
        loading.value = true;
        goFetch< SectorWithRelatedSecurities, PersistedQueryPayload >( {
            serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
            endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
            payload: {
                id: `20006cf38b496b6f35cf9a0cdcac8d53`,
                variables: {
                    date: dateAsString,
                    symbol: symbol
                }
            },
            authToken: authToken.value
        } ).then( ( response: ApiFetchResponse< SectorWithRelatedSecurities > | null ) => {
            if ( response?.data?.relatedSecuritiesForSector ) {
                sectorRelatedData.value = buildRelatedData( response.data.relatedSecuritiesForSector );
            } else {
                reset();
            }
        } ).finally( () => loading.value = false );
    }

    return {
        // ACTIONS
        loadSectorDataForDate,
        loadSecurityDataForDate,
        reset,

        // GETTERS
        isLoading,
        relatedDataBySector,
        sectorRelatedData
    };
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSecuritiesStore, import.meta.hot ) );
}